/* TheMeowPicks Finder engine v3 — filter → rank.
   Works in the browser (window.TMPFinder) and in Node (module.exports).
   Answers shape:
   { need:'scratch'|'climb'|'sleep'|'litter',
     orientation:'vertical'|'horizontal'|'both'|'unsure'|null,   // only when need==='scratch'
     count:'one'|'multi',
     chips:['kitten'|'senior'|'large'],                          // may be empty
     wall:'yes'|'no'|'prefer_not',
     space:['tight'|'furniture'],                                 // may be empty
     budget:'b1'|'b2'|'b3'|'b4'|'any',
     style:'wood'|'minimal'|'soft'|'any' }
*/
(function(root){
  const NEED_LABEL={scratch:'scratching',climb:'climbing',sleep:'sleeping',litter:'litter'};
  const STYLE_LABEL={wood:'warm wood',minimal:'minimal black or white',soft:'a soft, sculptural form'};
  const FOOT={small:0,medium:1,large:2};

  function nice(n){ return Math.round(n/50)*50; }

  /* Budget bands from the catalog's own price distribution (quartiles, rounded to $50). */
  function computeBands(products){
    const p=products.map(x=>x.price_usd).sort((a,b)=>a-b);
    const q=k=>p[Math.floor((p.length-1)*k)];
    let e1=nice(q(0.25)), e2=nice(q(0.5)), e3=nice(q(0.75));
    if(e1<50)e1=50; if(e2<=e1)e2=e1+100; if(e3<=e2)e3=e2+150;
    return [
      {id:'b1',min:0,max:e1,label:`Under $${e1}`},
      {id:'b2',min:e1,max:e2,label:`$${e1} to $${e2}`},
      {id:'b3',min:e2,max:e3,label:`$${e2} to $${e3}`},
      {id:'b4',min:e3,max:Infinity,label:`$${e3} and up`}
    ];
  }
  function bandOf(bands,id){ return bands.find(b=>b.id===id); }
  function inBand(b,price){ return price>=b.min && price<b.max; }
  function fmt(n){ return '$'+(Number.isInteger(n)?n:n.toFixed(2)); }

  function eligible(products,a,bands,relax){
    relax=relax||{};
    let list=products.filter(p=>p.need_primary===a.need||(p.need_secondary||[]).includes(a.need));
    if(a.wall==='no') list=list.filter(p=>p.mount!=='wall');
    const chips=a.chips||[];
    if(chips.includes('senior')) list=list.filter(p=>p.senior_access);
    if(chips.includes('kitten')) list=list.filter(p=>p.kitten_ok);
    if(chips.includes('large')) list=list.filter(p=>p.large_cat_verified!==false);
    if(a.need==='scratch'&&!relax.orientation){
      if(a.orientation==='vertical') list=list.filter(p=>p.scratch_orientation==='vertical'||p.scratch_orientation==='both'||p.scratch_orientation===null);
      if(a.orientation==='horizontal') list=list.filter(p=>p.scratch_orientation==='horizontal'||p.scratch_orientation==='both');
    }
    if(a.need==='litter'&&a.enclosure&&a.enclosure!=='either'&&!relax.enclosure){
      list=list.filter(p=>!p.litter_style||p.litter_style===a.enclosure);
    }
    if(a.budget&&a.budget!=='any'&&!relax.budget){
      const b=bandOf(bands,a.budget); list=list.filter(p=>inBand(b,p.price_usd));
    }
    return list;
  }

  function score(p,a,bands,ctx){
    let s=0;
    /* cat & behaviour, 30 */
    s+= p.need_primary===a.need?30:18;
    if(a.count==='multi') s+= p.multi_cat_capacity>=3?4:(p.multi_cat_capacity===2?0:-6);
    if((a.chips||[]).includes('large')) s+= p.large_cat_verified===true?4:-4;
    if(a.need==='scratch'&&a.orientation==='unsure'&&p.scratch_orientation==='both') s+=3;
    /* space, 25 */
    const sp=a.space||[];
    if(sp.includes('tight')) s+= [25,10,0][FOOT[p.footprint]]; else s+=12;
    if(sp.includes('furniture')) s+= p.furniture_grade?15:0;
    if(a.wall==='prefer_not'&&p.mount==='wall') s-=12;
    /* budget, 20 */
    if(a.budget==='any') s+=12;
    else { const b=bandOf(bands,a.budget); if(inBand(b,p.price_usd)) s+=20; else s+= 6 - Math.min(6, Math.abs(p.price_usd-(b.max===Infinity?b.min:b.max))/100); }
    /* style, 15 */
    if(a.style==='any') s+=9; else if((p.style||[]).includes(a.style)) s+=15;
    /* editorial, 10 */
    s+= Math.max(2, 10-(p.editorial_rank-1)*1.5);
    return Math.round(s*10)/10;
  }

  function makeCmp(a){
    const large=(a.chips||[]).includes('large');
    return (x,y)=>
      ((y.p.need_primary===a.need)-(x.p.need_primary===a.need)) ||
      (large ? ((y.p.large_cat_verified===true)-(x.p.large_cat_verified===true)) : 0) ||
      (y.score-x.score) || (x.p.editorial_rank-y.p.editorial_rank) || (x.p.price_usd-y.p.price_usd) || (x.p.id<y.p.id?-1:1);
  }

  function distinction(alt,pick,a){
    if(alt.price_usd<pick.price_usd*0.8) return `Less to spend, ${fmt(alt.price_usd)}`;
    if(alt.mount!==pick.mount) return alt.mount==='wall'?'If wall mounting is an option':'If you would rather not drill';
    if(FOOT[alt.footprint]<FOOT[pick.footprint]) return 'Better if space is tighter';
    if(alt.furniture_grade&&!pick.furniture_grade) return 'Reads more like furniture';
    if(alt.multi_cat_capacity>pick.multi_cat_capacity) return 'More room for two cats';
    if(alt.senior_access&&!pick.senior_access) return 'Easier for an older cat';
    const st=(alt.style||[]).find(x=>!(pick.style||[]).includes(x));
    if(st) return `In ${STYLE_LABEL[st]} instead`;
    if(alt.price_usd>pick.price_usd*1.25) return `A step up, ${fmt(alt.price_usd)}`;
    return 'The closest alternative';
  }

  function whyLines(p,a,bands,ctx){
    const L=[]; const chips=a.chips||[], sp=a.space||[];
    if(a.wall==='no'&&p.mount!=='wall') L.push('Freestanding, so nothing goes into your walls.');
    if(sp.includes('tight')&&p.footprint==='small') L.push('One of the smallest footprints in this category.');
    if(sp.includes('furniture')&&p.furniture_grade) L.push('Passes as furniture from across the room.');
    if(chips.includes('senior')&&p.senior_access) L.push('No jump needed to use it.');
    if(chips.includes('large')&&p.large_cat_verified===true) L.push('Rated by the maker for a heavy cat.');
    if(a.count==='multi'&&p.multi_cat_capacity>=2) L.push('Room for two without a standoff.');
    if(a.budget!=='any'&&!ctx.budgetRelaxed) L.push(`Within your range at ${fmt(p.price_usd)}.`);
    if(a.style!=='any'&&(p.style||[]).includes(a.style)) L.push(`In ${STYLE_LABEL[a.style]}, as asked.`);
    return L.slice(0,3);
  }
  function watchLines(p,a,ctx){
    const L=[]; if(p.finder_watchout) L.push(p.finder_watchout);
    if(p.mount==='wall'&&!/wall/i.test(p.finder_watchout||'')) L.push('Wall mounted: it needs studs or proper anchors.');
    if((a.chips||[]).includes('large')&&p.large_cat_verified===null) L.push('Not yet verified by the maker for cats over 12 lb; check the weight limit before buying.');
    if(ctx.budgetRelaxed) L.push(`Over your range at ${fmt(p.price_usd)}.`);
    return L;
  }
  function dims(p){
    const d=[]; if(p.height_cm)d.push(`H ${p.height_cm} cm`); if(p.width_cm)d.push(`W ${p.width_cm} cm`); if(p.depth_cm&&p.depth_cm!==p.width_cm)d.push(`D ${p.depth_cm} cm`);
    return d.join(' · ');
  }

  function recommend(products,a,bands){
    bands=bands||computeBands(products);
    const ctx={budgetRelaxed:false,orientationRelaxed:false,note:null};
    const hasBudget=a.budget&&a.budget!=='any';
    let list=eligible(products,a,bands,{});
    /* a primary just outside the band beats a secondary inside it */
    const under=(arr)=>{ const b=bandOf(bands,a.budget); const u=arr.filter(p=>p.price_usd<b.max); return u.length?u:arr; };
    if(hasBudget&&!list.some(p=>p.need_primary===a.need)){
      const relaxedPrim=under(eligible(products,a,bands,{budget:true}).filter(p=>p.need_primary===a.need));
      if(relaxedPrim.length){ list=list.concat(relaxedPrim.filter(p=>!list.includes(p))); ctx.budgetRelaxed=true; }
    }
    if(!list.length&&hasBudget){ list=under(eligible(products,a,bands,{budget:true})); if(list.length) ctx.budgetRelaxed=true; }
    if(!list.length&&a.need==='scratch'){ list=eligible(products,a,bands,{budget:true,orientation:true}); if(list.length){ctx.orientationRelaxed=true;ctx.budgetRelaxed=hasBudget;} }
    if(!list.length&&a.need==='litter'&&a.enclosure&&a.enclosure!=='either'){ list=eligible(products,a,bands,{budget:true,enclosure:true}); if(list.length){ctx.enclosureRelaxed=true;ctx.budgetRelaxed=hasBudget;} }
    if(!list.length){
      let note='Nothing in the edit fits all of that yet. Loosen one answer, or write to us and we will look for it.';
      if(a.need==='litter'&&(a.chips||[]).includes('kitten')) note='The self-cleaning boxes we carry are not for kittens under six months, per the makers. Come back once they are older, or start with a simple tray and the litter accessories for now.';
      return {pick:null,alternatives:[],note,bands};
    }
    const cmp=makeCmp(a);
    const ranked=list.map(p=>({p,score:score(p,a,bands,ctx)})).sort(cmp);
    const pick=ranked[0].p;
    const b=hasBudget?bandOf(bands,a.budget):null;
    const pickOut=b&&!inBand(b,pick.price_usd);
    if(pickOut){
      if(pick.price_usd<b.min) ctx.note=`Nothing in the edit lands between $${b.min} and ${b.max===Infinity?'up':'$'+b.max} for ${NEED_LABEL[a.need]}; this is the nearest, at ${fmt(pick.price_usd)}, under your range.`;
      else ctx.note=`Closest fit: ${fmt(pick.price_usd)}. We don't carry a ${NEED_LABEL[a.need]} piece under $${b.max} that we would recommend.`;
    }
    if(ctx.orientationRelaxed) ctx.note=(ctx.note?ctx.note+' ':'')+`We have no ${a.orientation} scratcher that fits the rest of your answers; this is the nearest.`;
    if(ctx.enclosureRelaxed) ctx.note=(ctx.note?ctx.note+' ':'')+`We don't carry an ${a.enclosure==='open'?'open-top':'enclosed'} unit that fits the rest of your answers; this is the nearest.`;
    /* alternatives: next two, backfilled from just outside the band when the band is thin */
    let alts=ranked.slice(1,3).map(r=>r.p);
    if(alts.length<2&&hasBudget){
      const extra=eligible(products,a,bands,{budget:true,orientation:ctx.orientationRelaxed}).filter(p=>p!==pick&&!alts.includes(p))
        .map(p=>({p,score:score(p,a,bands,ctx)})).sort(cmp).map(r=>r.p);
      alts=alts.concat(extra).slice(0,2);
    }
    const pctx={budgetRelaxed:pickOut};
    const alternatives=alts.map(p=>({product:p,distinction:distinction(p,pick,a),outOfBand:b?!inBand(b,p.price_usd):false}));
    return {pick,pickScore:ranked[0].score,why:whyLines(pick,a,bands,pctx),watch:watchLines(pick,a,pctx),dims:dims(pick),
            alternatives,note:ctx.note,eligibleCount:list.length,bands,tieAtTop:ranked.length>1&&ranked[1].score===ranked[0].score&&ranked[1].p.need_primary===pick.need_primary};
  }

  const api={recommend,computeBands,eligible,score,NEED_LABEL};
  if(typeof module!=='undefined'&&module.exports) module.exports=api; else root.TMPFinder=api;
})(typeof window!=='undefined'?window:globalThis);